import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';

import BasePage from '../../components/BasePage';
import DataTable, { Column, FilterField } from '../../components/DataTable';
import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/useToast';

import {
  StatusBadge,
  IconButton,
  CreateButton,
  CategoryChip,
  Chips,
} from './styles';

interface ICategory {
  id: string;
  title: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

interface IUser {
  id: string;
  first_name: string;
  last_name: string;
}

interface IPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  slug: string;
  status: string;
  type: string;
  author_id: string | null;
  featured_image: string;
  publishedAt: Date;
  categories: ICategory[];
  author: {
    first_name: string;
    last_name: string;
  } | null;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'revision', label: 'Revisão pendente' },
  { value: 'published', label: 'Publicado' },
];

const TYPE_OPTIONS = [{ value: 'default', label: 'Padrão' }];

const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  published: 'success',
  revision: 'warning',
  draft: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  revision: 'Revisão pendente',
};

const News: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<IPost[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});
  const [reloadToken, setReloadToken] = useState(0);

  const [unions, setUnions] = useState<ISyndicate[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);

  useEffect(() => {
    document.title = 'Notícias - Viva Voz - Rede Suco de Laranja';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get<IPost[]>('/admin/posts', {
          params: { ...filterParams, page: currentPage },
        });
        if (cancelled) return;
        setPosts(response.data);
        setTotal(Number(response.headers['x-total-posts'] || 0));
      } catch {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao carregar as notícias.',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filterParams, currentPage, reloadToken, addToast]);

  useEffect(() => {
    const load = async () => {
      try {
        const usersRes = await api.get<IUser[]>('/users');
        setUsers(usersRes.data);
        if (user.role === 'admin') {
          const unionsRes = await api.get<ISyndicate[]>('/unions');
          setUnions(unionsRes.data);
        }
      } catch {
        /* silent */
      }
    };
    load();
  }, [user.role]);

  const handleDelete = useCallback(
    async (id: string) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Deseja realmente remover esta notícia?')) return;
      try {
        await api.delete(`/news/${id}`);
        addToast({
          title: 'Sucesso',
          body: 'Notícia removida com sucesso.',
        });
        setReloadToken(t => t + 1);
      } catch {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar remover a notícia.',
        });
      }
    },
    [addToast],
  );

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'title',
        label: 'Título',
        type: 'text',
        placeholder: 'Digite o título',
        span: 4,
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Todos',
        options: STATUS_OPTIONS,
        span: 2,
      },
      {
        key: 'type',
        label: 'Tipo',
        type: 'select',
        placeholder: 'Todos',
        options: TYPE_OPTIONS,
        span: 2,
      },
      {
        key: 'author_id',
        label: 'Autor',
        type: 'select',
        placeholder: 'Todos',
        options: users.map(u => ({
          value: u.id,
          label: `${u.first_name} ${u.last_name}`,
        })),
        span: user.role === 'admin' ? 2 : 4,
      },
    ];

    if (user.role === 'admin') {
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: unions.map(u => ({ value: u.id, label: u.nome_fantasia })),
        span: 2,
      });
    }

    return base;
  }, [users, unions, user.role]);

  const columns = useMemo<Column<IPost>[]>(
    () => [
      {
        key: 'title',
        label: 'Título',
        render: p => (
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{p.title}</span>
        ),
      },
      {
        key: 'author',
        label: 'Autor',
        width: '15%',
        render: p =>
          p.author ? `${p.author.first_name} ${p.author.last_name}` : '—',
      },
      {
        key: 'categories',
        label: 'Categorias',
        width: '22%',
        render: p =>
          p.categories.length === 0 ? (
            '—'
          ) : (
            <Chips>
              {p.categories.map(c => (
                <CategoryChip key={c.id}>{c.title}</CategoryChip>
              ))}
            </Chips>
          ),
      },
      {
        key: 'status',
        label: 'Status',
        width: '12%',
        render: p => (
          <StatusBadge $tone={STATUS_TONE[p.status] || 'neutral'}>
            {STATUS_LABEL[p.status] || p.status}
          </StatusBadge>
        ),
      },
      {
        key: 'publishedAt',
        label: 'Publicação',
        width: '15%',
        render: p =>
          p.publishedAt
            ? format(new Date(p.publishedAt), "dd/MM/yyyy 'às' HH:mm")
            : '—',
      },
    ],
    [],
  );

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  return (
    <BasePage title="Notícias">
      <Container fluid style={{ padding: 0 }}>
        <DataTable<IPost>
          title="Notícias"
          subtitle="Publique, revise e remova notícias da plataforma"
          columns={columns}
          data={posts}
          rowKey="id"
          loading={loading}
          page={currentPage}
          total={total}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <Link to="/news/create" style={{ textDecoration: 'none' }}>
              <CreateButton type="button">
                <FiPlus size={14} />
                Nova notícia
              </CreateButton>
            </Link>
          )}
          rowActions={row => (
            <>
              <IconButton
                type="button"
                title="Editar"
                onClick={() => history.push(`/news/${row.id}`)}
              >
                <FiEdit size={14} />
              </IconButton>
              <IconButton
                type="button"
                title="Remover"
                $danger
                onClick={() => handleDelete(row.id)}
              >
                <FiTrash2 size={14} />
              </IconButton>
            </>
          )}
          emptyMessage="Nenhuma notícia encontrada."
        />
      </Container>
    </BasePage>
  );
};

export default News;
